import React, { useEffect, useMemo, useState } from "react";
import ResetButton from "@/components/ResetButton";
import useDraft from "@/lib/useDraft";
import { Mic, AudioLines, Languages, Lock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ModelPicker from "@/components/ModelPicker";
import GenerationGrid from "@/components/GenerationGrid";
import GenerateButton, { useCost } from "@/components/GenerateButton";
import UploadZone from "@/components/UploadZone";
import { Field } from "@/components/Seg";
import { useAttachments } from "@/lib/useUploader";

const TABS = [
  { id: "tts", label: "Text to Speech", icon: Mic },
  { id: "voice_change", label: "Voice Change", icon: AudioLines },
  { id: "translate", label: "Translate", icon: Languages },
];
const LANGS = [["en", "English"], ["hi", "Hindi"], ["es", "Spanish"], ["fr", "French"], ["de", "German"], ["it", "Italian"], ["pt", "Portuguese"],
  ["ja", "Japanese"], ["ko", "Korean"], ["zh", "Chinese"], ["ar", "Arabic"], ["ru", "Russian"], ["ta", "Tamil"], ["te", "Telugu"], ["bn", "Bengali"],
  ["mr", "Marathi"], ["gu", "Gujarati"], ["kn", "Kannada"], ["ml", "Malayalam"], ["pa", "Punjabi"], ["ur", "Urdu"], ["tr", "Turkish"], ["id", "Indonesian"]];
const VOICES = ["Narrator (warm)", "Studio female", "Studio male", "Energetic", "Calm"];

export default function AudioStudio({ projectId, models, configured, items, references = [], onUploaded, generate, onOpen, onRetry, seed, header, onChanged, modelPick, onReset}) {
  const [tab, setTab] = useState("tts");
  const [model, setModel] = useState(null);
  const [text, setText] = useDraft(`ax_draft_${projectId}_audio`);
  const [voice, setVoice] = useState(VOICES[0]);
  const [lang, setLang] = useState("hi");
  const [busy, setBusy] = useState(false);
  const media = useAttachments(projectId, 1, onUploaded, true, "audio-media");

  const pool = useMemo(() => models.filter((m) => m.section === "audio" && m.modes.includes(tab)), [models, tab]);
  useEffect(() => { setModel(pool.find((m) => m.available) || pool[0] || null); media.setItems([]); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool]);
  const picked = React.useRef(null);
  useEffect(() => {
    const m = modelPick && models.find((x) => x.id === modelPick.id && x.section === "audio");
    if (!m) return;
    picked.current = m;
    if (!m.modes.includes(tab)) setTab(m.modes[0]); else setModel(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPick, models]);
  useEffect(() => { if (picked.current && pool.some((x) => x.id === picked.current.id)) { const m = picked.current; picked.current = null; setTimeout(() => setModel(m), 0); } }, [pool]);
  useEffect(() => {
    if (!seed) return;
    if (seed.mode && TABS.some((t) => t.id === seed.mode)) setTab(seed.mode);
    if (seed.prompt) setText(seed.prompt);
    const s = seed.settings || {};
    if (s.voice) setVoice(s.voice);
    if (s.target_language) setLang(s.target_language);
    const ref = references.find((r) => r.id === (seed.video_id || seed.audio_id));
    setTimeout(() => {
      if (ref) media.setItems([ref]);
      const m = models.find((x) => x.id === seed.model_id && x.section === "audio");
      if (m) setModel(m);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);
  const anyLocked = pool.length > 0 && pool.every((m) => !m.available);
  const dur = media.items[0]?.duration;
  const settings = { duration: tab === "tts" ? Math.max(2, Math.ceil(text.length / 15)) : Math.ceil(dur || 2), ...(tab === "translate" && { target_language: lang }), ...(tab === "tts" && { voice }) };
  const usd = useCost(model?.available && model.id, settings, tab === "tts" ? !!text.trim() : media.ids.length > 0);

  const reason = !configured ? "Coming Soon" : !model?.available ? "Not available yet"
    : media.uploading ? "Waiting for upload…" : tab === "tts" && !text.trim() ? "Type the text to speak"
    : tab === "tts" && text.length > (model?.max_prompt || 5000) ? `Text too long — max ${model?.max_prompt || 5000} characters`
    : tab !== "tts" && !media.ids.length ? (tab === "translate" ? "Add a video to translate" : "Add a voice recording") : "";

  const go = async () => {
    setBusy(true);
    await generate({ section: "audio", mode: tab, model_id: model.id, prompt: tab === "tts" ? text : "", settings,
      video_id: tab === "translate" ? media.ids[0] : null, audio_id: tab === "voice_change" ? media.ids[0] : null, media_duration: dur });
    setBusy(false);
  };

  return (
    <div className="flex-1 flex min-h-0">
      <aside className="w-[380px] shrink-0 border-r border-white/5 bg-[#0B1013]/70 backdrop-blur-xl flex flex-col min-h-0" data-testid="audio-sidebar">
        <div className="px-3 pt-3 flex justify-end"><ResetButton onReset={onReset} testid="audio-panel-reset" /></div>
        <div className="p-3 grid grid-cols-3 gap-1 border-b border-zinc-800/80">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`audio-tab-${t.id}`}
              className={`flex flex-col items-center gap-1 py-2 rounded-lg text-[11px] font-medium ${tab === t.id ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-white hover:bg-zinc-800/50"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {anyLocked && (
            <div className="rounded-xl border border-zinc-700 bg-zinc-900/60 p-3 flex gap-2.5 text-xs text-zinc-300 leading-relaxed" data-testid="audio-locked-banner">
              <Lock className="w-4 h-4 shrink-0 text-zinc-400 mt-0.5" />
              <span>{tab === "tts" ? "Text-to-speech" : "Voice-change"} models are coming soon to BFX. They will unlock here automatically. <b>Translate</b> already works.</span>
            </div>
          )}
          <Field label="Model">
            <ModelPicker models={models} value={model?.id} onChange={setModel} configured={configured} testid="audio-model"
              groups={[{ label: "Audio Models", filter: (m) => m.section === "audio" && m.modes.includes(tab) }]} />
          </Field>
          {tab === "tts" && (<>
            <Field label="Text" hint={`${text.length.toLocaleString()} / ${(model?.max_prompt || 5000).toLocaleString()} characters`}>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} disabled={anyLocked} data-testid="audio-tts-input"
                placeholder="Type what the voice should say…" className="ax-input resize-none disabled:opacity-40" />
            </Field>
            <Field label="Voice">
              <Select value={voice} onValueChange={setVoice} disabled={anyLocked}>
                <SelectTrigger className="ax-input h-10" data-testid="audio-voice-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800 text-zinc-200">{VOICES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </>)}
          {tab === "voice_change" && (
            <UploadZone testid="audio-voice-zone" title="Add a voice recording" badge="Required" subtitle="MP3/WAV — we keep the words, change the voice"
              accept="audio/*" max={1} items={media.items} onFiles={media.add} onRemove={media.remove} disabled={anyLocked} />
          )}
          {tab === "translate" && (<>
            <UploadZone testid="audio-translate-zone" title="Add a video to translate" badge="Up to 10 min" subtitle="The speech is translated and dubbed in the same voice"
              accept="video/*" max={1} items={media.items} onFiles={media.add} onRemove={media.remove} />
            <Field label="Translate to">
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="ax-input h-10" data-testid="audio-lang-trigger"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800 text-zinc-200 max-h-72">{LANGS.map(([c, n]) => <SelectItem key={c} value={c} data-testid={`audio-lang-${c}`}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {dur ? <p className="text-[11px] text-zinc-500">Billed for {Math.ceil(dur)} s — the length of your video.</p> : null}
          </>)}
        </div>
        <div className="p-4 border-t border-zinc-800/80">
          <GenerateButton usd={usd} onClick={go} busy={busy} disabledReason={reason} testid="audio-generate" className="w-full" />
          {reason && <p className="text-[11px] text-zinc-500 text-center mt-2" data-testid="audio-generate-hint">{reason}</p>}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-6" data-testid="audio-history">
        {header}
        <GenerationGrid items={items} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged} cols="grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3"
          emptyTitle="No audio yet" emptyHint="Translate a video into another language now, or come back when voice models arrive." />
      </main>
    </div>
  );
}
